/*
 This file is part of GNU Taler
 (C) 2022 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import { TalerErrorDetail } from "@gnu-taler/taler-util";
// import { TalerError } from "@gnu-taler/taler-wallet-core";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";

const EMPTY_DEPENDENCIES: any[] = [];

function useDependencyMarker(dependencies: any[] | undefined): object {
  const current = useRef<{ dependencies: any[]; marker: object }>();
  const nextDependencies = dependencies ?? EMPTY_DEPENDENCIES;
  if (
    !current.current ||
    current.current.dependencies.length !== nextDependencies.length ||
    current.current.dependencies.some(
      (previous, index) => !Object.is(previous, nextDependencies[index]),
    )
  ) {
    current.current = { dependencies: nextDependencies, marker: {} };
  }
  return current.current.marker;
}

export interface HookOk<T> {
  hasError: false;
  response: T;
}

export type HookError = HookGenericError | HookOperationalError;

export interface HookGenericError {
  hasError: true;
  operational: false;
  message: string;
}

export interface HookOperationalError {
  hasError: true;
  operational: true;
  details: TalerErrorDetail;
}

interface WithRetry {
  retry: () => void;
}

export type HookResponse<T> = HookOk<T> | HookError | undefined;
export type HookResponseWithRetry<T> =
  | ((HookOk<T> | HookError) & WithRetry)
  | undefined;

/** Convert an asynchronous request into a render-friendly response state. */
export function useAsyncAsHook<T>(
  fn: () => Promise<T | false>,
  deps?: any[],
): HookResponseWithRetry<T> {
  const [result, setHookResponse] = useState<HookResponse<T>>(undefined);
  const [retryCounter, setRetryCounter] = useState(0);
  const generation = useRef(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const dependencyMarker = useDependencyMarker(deps);

  useEffect(() => {
    const currentGeneration = ++generation.current;
    let active = true;
    setHookResponse(undefined);
    fnRef.current().then(
      (response) => {
        if (!active || generation.current !== currentGeneration) return;
        if (response !== false) {
          setHookResponse({ hasError: false, response });
        }
      },
      (error: unknown) => {
        if (!active || generation.current !== currentGeneration) return;
        setHookResponse({
          hasError: true,
          operational: false,
          message: error instanceof Error ? error.message : String(error),
        });
      },
    );
    return () => {
      active = false;
    };
  }, [dependencyMarker, retryCounter]);

  const retry = useCallback(() => {
    setRetryCounter((counter) => counter + 1);
  }, []);

  if (!result) return undefined;
  return { ...result, retry };
}
